'use client';

/**
 * AstorHighlightOverlay Component
 *
 * Renders a visual highlight around a target element identified by CSS selector.
 * Shows a pulsing glow border and an optional tooltip message near the element.
 * Auto-dismisses after the configured duration (context handles the timer).
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import { alpha, Box, Portal, Typography, useTheme } from '@mui/material';
import type { HighlightAction } from '@ops-matrix/shared-types';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { glassEffects } from '../../theme/shadows';

// ============================================================================
// TYPES
// ============================================================================

export interface AstorHighlightOverlayProps {
  /** The highlight action describing what to highlight */
  highlight: HighlightAction;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const HIGHLIGHT_PADDING = 8;
const TOOLTIP_GAP = 12;
const TOOLTIP_MAX_WIDTH = 280;

// ============================================================================
// COMPONENT
// ============================================================================

export function AstorHighlightOverlay({ highlight }: AstorHighlightOverlayProps): React.JSX.Element | null {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  // Find and track the target element position
  useEffect(() => {
    const updateRect = () => {
      const el = document.querySelector(highlight.selector);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
      }
    };

    updateRect();

    // Scroll to element if it's off-screen
    const el = document.querySelector(highlight.selector);
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Re-measure after scroll
      scrollTimeout = setTimeout(updateRect, 350);
    }

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [highlight.selector]);

  if (!targetRect) return null;

  const tooltipTop = targetRect.bottom + HIGHLIGHT_PADDING + TOOLTIP_GAP;
  const tooltipLeft = Math.max(
    16,
    Math.min(
      targetRect.left + targetRect.width / 2 - TOOLTIP_MAX_WIDTH / 2,
      window.innerWidth - TOOLTIP_MAX_WIDTH - 16,
    ),
  );

  return (
    <Portal>
      <AnimatePresence>
        {/* Semi-transparent overlay */}
        <motion.div
          key="highlight-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1299,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'none',
          }}
        />

        {/* Highlight ring around target element */}
        <motion.div
          key="highlight-ring"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.25, ease: [0.215, 0.61, 0.355, 1] }}
          style={{
            position: 'fixed',
            zIndex: 1300,
            top: targetRect.top - HIGHLIGHT_PADDING,
            left: targetRect.left - HIGHLIGHT_PADDING,
            width: targetRect.width + HIGHLIGHT_PADDING * 2,
            height: targetRect.height + HIGHLIGHT_PADDING * 2,
            borderRadius: 12,
            border: `2px solid ${theme.palette.primary.main}`,
            boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`,
            pointerEvents: 'none',
          }}
        />

        {/* Tooltip with message */}
        {highlight.message && (
          <motion.div
            key="highlight-tooltip"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            style={{
              position: 'fixed',
              zIndex: 1301,
              top: tooltipTop,
              left: tooltipLeft,
              maxWidth: TOOLTIP_MAX_WIDTH,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                background: glass.background,
                backdropFilter: glass.backdropBlur,
                WebkitBackdropFilter: glass.backdropBlur,
                border: `1px solid ${glass.border}`,
                boxShadow: `0 8px 24px -4px rgba(0, 0, 0, 0.3)`,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                {highlight.message}
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
}

AstorHighlightOverlay.displayName = 'AstorHighlightOverlay';

export default AstorHighlightOverlay;
